/**
 * Encrypts a payload using hybrid encryption to match the server-side decryptPayload():
 *   1. Generate a random 32-byte AES key and 16-byte IV.
 *   2. Encrypt the JSON-stringified payload with AES-256-CBC.
 *   3. Encrypt the AES key with the server's RSA public key (RSA-OAEP / SHA-256).
 *
 * @param {object} payload - The data to encrypt
 * @param {string} publicKeyPem - RSA public key in PEM format (SPKI / "BEGIN PUBLIC KEY")
 * @returns {Promise<{ encryptedKey: string, iv: string, encryptedPayload: string }>}
 */
export async function encryptPayload(payload, publicKeyPem) {
    const rsaKey = await importRsaPublicKey(publicKeyPem);

    // Generate a random AES-256 key and 16-byte IV
    const aesKey = await window.crypto.subtle.generateKey(
        { name: 'AES-CBC', length: 256 },
        true,       // must be exportable so we can RSA-wrap it
        ['encrypt']
    );
    const iv = window.crypto.getRandomValues(new Uint8Array(16));

    // Encrypt the payload with AES-256-CBC
    const encodedPayload = new TextEncoder().encode(JSON.stringify(payload));
    const encryptedPayloadBuf = await window.crypto.subtle.encrypt(
        { name: 'AES-CBC', iv },
        aesKey,
        encodedPayload
    );

    // Export raw AES key bytes, then RSA-OAEP encrypt them
    const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
    const encryptedKeyBuf = await window.crypto.subtle.encrypt(
        { name: 'RSA-OAEP' },
        rsaKey,
        rawAesKey
    );

    return {
        encryptedKey:     bufToBase64(encryptedKeyBuf),
        iv:               bufToBase64(iv.buffer),
        encryptedPayload: bufToBase64(encryptedPayloadBuf),
    };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function bufToBase64(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

async function importRsaPublicKey(pem) {
    const b64 = pem
        .replace(/\\n/g, '\n')                      // handle literal \n from .env files
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s+/g, '');

    const derBuffer = Uint8Array.from(atob(b64), c => c.charCodeAt(0));

    return window.crypto.subtle.importKey(
        'spki',
        derBuffer.buffer,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt']
    );
}
